import LockedStrategyPreview from '../components/strategy/LockedStrategyPreview';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WelcomeUnlockedBanner from '../components/strategy/WelcomeUnlockedBanner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, 
  CheckCircle2, 
  ChevronRight,
  Sparkles,
  AlertCircle,
  Clock,
  Lock
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

// Toasts on this page appear top-center so they don't cover the header progress (top-right)
const TOAST_OPTS = { position: 'top-center' };
const notify = {
  success: (msg) => toast.success(msg, TOAST_OPTS),
  error: (msg) => toast.error(msg, TOAST_OPTS),
  info: (msg) => toast.info(msg, TOAST_OPTS),
};

// Question metadata for sidebar display
const QUESTION_LABELS = {
  'q1_complexity': 'Tax Filing Complexity',
  'q2_income_source': 'Primary Income Source',
  'q3_agi': 'Income Range (AGI)',
  'q4_home_office': 'Home Office Usage',
  'q5_cpa_status': 'CPA Relationship',
  'q6_retirement_accounts': 'Retirement Accounts',
  'q7_retirement_interest': 'Retirement Interest',
  'q8_vehicle_use': 'Business Vehicle Use',
  'q9_mileage_tracking': 'Mileage Tracking',
  'q10_health_insurance': 'Health Insurance',
  'q10a_quarterly_estimates': 'Quarterly Estimated Taxes',
  'q10b_estimate_method': 'Estimate Calculation',
  'q10c_prior_year_balance': 'Last Year\'s Balance Due',
  'q11_property': 'Property Ownership',
  'q12_rental_involvement': 'Rental Property Management',
  'q13_life_events': 'Life Changes',
  'q14_entity_structure': 'Business Entity',
  'q15_entity_election': 'S-Corp Election',
  'q15a_scorp_salary': 'S-Corp Owner Pay',
  'q15b_scorp_salary_share': 'S-Corp Salary Share',
  'q16_multi_state': 'Multi-State Tax',
  'q17_prior_strategy': 'Prior Tax Strategies',
  'q18_additional_topics': 'Additional Topics'
};

const StrategyAdvisor = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showWelcome, setShowWelcome] = useState(searchParams.get('welcome') === '1');
  const dismissWelcome = () => { setShowWelcome(false); searchParams.delete('welcome'); setSearchParams(searchParams, { replace: true }); };
  
  const [loading, setLoading] = useState(true);
  const [interview, setInterview] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [generating, setGenerating] = useState(false);

  // Options that mean "nothing applies" — mutually exclusive with other selections
  const EXCLUSIVE_VALUES = ['none', 'none_yet'];

  const isAnswerValid = (question, answer) => {
    if (!question) return false;
    if (question.question_type === 'multi_select') return Array.isArray(answer) && answer.length > 0;
    if (question.question_type === 'text') return typeof answer === 'string' && answer.trim().length > 0;
    return typeof answer === 'string' && answer.length > 0;
  };

  const toggleMultiSelect = (value, checked) => {
    const current = Array.isArray(selectedAnswer) ? selectedAnswer : [];
    if (!checked) {
      setSelectedAnswer(current.filter(v => v !== value));
    } else if (EXCLUSIVE_VALUES.includes(value)) {
      setSelectedAnswer([value]);
    } else {
      setSelectedAnswer([...current.filter(v => !EXCLUSIVE_VALUES.includes(v)), value]);
    }
  };

  const applyMetrics = (data) => {
    setMetrics({
      questions_answered: data.questions_answered,
      questions_actually_shown: data.questions_actually_shown,
      questions_allocated: data.questions_allocated,
      completeness_for_gate: data.completeness_for_gate,
      completeness_for_ui: data.completeness_for_ui,
      meets_threshold: data.meets_threshold
    });
    if (Array.isArray(data.answered_questions)) {
      setAnsweredQuestions(data.answered_questions);
    }
  };

  const errorMessage = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    return fallback;
  };

  const initializedForUser = useRef(null);
  const [accessDenied, setAccessDenied] = useState(null); // null | { message }

  // Check paid access (server-side: any active paid plan or grandfathered) + start/resume — once per user
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (initializedForUser.current === user.user_id) return;
    initializedForUser.current = user.user_id;

    const init = async () => {
      try {
        const { data } = await axios.get(`${API}/api/strategy_interview/access`, { withCredentials: true });
        if (!data.has_access) {
          setAccessDenied({ message: data.message });
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Failed to check Strategy Advisor access:', error);
        // Fall through — /start enforces access server-side and returns 403 if not allowed
      }
      startOrResumeInterview();
    };
    init();
  }, [user?.user_id, navigate]);

  const startOrResumeInterview = async () => {
    try {
      const response = await axios.post(
        `${API}/api/strategy_interview/start`,
        {},
        { withCredentials: true }
      );
      
      setInterview(response.data);
      await loadStatus();
    } catch (error) {
      console.error('Failed to start interview:', error);
      if (error?.response?.status === 403) {
        setAccessDenied({ message: errorMessage(error, 'Strategy Advisor is included with every paid plan.') });
      } else {
        notify.error('Failed to load interview. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/api/strategy_interview/status`,
        { withCredentials: true }
      );
      
      setCurrentQuestion(response.data.current_question);
      setSelectedAnswer(null);
      // Server is the source of truth for answered questions (survives page reloads)
      applyMetrics(response.data);
    } catch (error) {
      console.error('Failed to load status:', error);
    }
  };
  
  const jumpToQuestion = async (questionId) => {
    try {
      const response = await axios.get(
        `${API}/api/strategy_interview/question/${questionId}`,
        { withCredentials: true }
      );
      
      // Load the question and pre-fill the answer
      setCurrentQuestion(response.data.question);
      setSelectedAnswer(response.data.saved_answer);
      
      // Scroll to top to show the question
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      notify.success('Question loaded for editing');
    } catch (error) {
      console.error('Failed to load question for editing:', error);
      notify.error('Failed to load question');
    }
  };

  const submitAnswer = async (overrideValue) => {
    const value = overrideValue !== undefined ? overrideValue : selectedAnswer;
    if (!currentQuestion || submitting) return;
    if (overrideValue === undefined && !isAnswerValid(currentQuestion, value)) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/api/strategy_interview/answer`,
        {
          question_id: currentQuestion.question_id,
          response: value
        },
        { withCredentials: true }
      );

      setCurrentQuestion(response.data.next_question);
      applyMetrics(response.data);
      setSelectedAnswer(null);
      if (response.data.pruned_question_ids?.length) {
        notify.info('Saved — some follow-up questions no longer apply and were removed');
      }
      // Normal saves need no toast: the sidebar updates immediately (avoids covering the header)
    } catch (error) {
      console.error('Failed to submit answer:', error);
      const detail = error?.response?.data?.detail;
      notify.error(errorMessage(error, 'Failed to save answer. Please try again.'));
      // Out-of-order / off-path answers: resync with the server's current question
      if (detail?.error === 'core_order' || detail?.error === 'question_not_applicable') {
        await loadStatus();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const generateReport = async () => {
    if (generating) return;
    if (!metrics?.meets_threshold) {
      notify.error(`Complete at least 60% of the interview to generate a report. Currently: ${metrics?.completeness_for_gate}%`);
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/api/strategy_interview/generate-report`,
        { interview_id: interview.interview_id },
        { withCredentials: true }
      );

      notify.success('Report generated successfully!');
      navigate(`/strategy-advisor/report/${response.data.report_id}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === 'insufficient_completeness') {
        notify.error(`${detail.message} (${detail.current_completeness}% complete)`);
      } else if (detail?.error === 'report_in_progress') {
        notify.info(detail.message);
      } else {
        notify.error('Failed to generate report. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const interviewComplete = !!metrics && metrics.questions_allocated > 0 && !currentQuestion;
  const coreAnswered = Math.min(metrics?.questions_answered || 0, 5);

  // Core step shown while allocation isn't locked yet ("Question N of 5")
  const coreStep = currentQuestion?.category === 'core'
    ? currentQuestion.question_number
    : Math.min((metrics?.questions_answered || 0) + 1, 5);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading interview...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return <LockedStrategyPreview />;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="strategy-advisor-page">
      {showWelcome && <WelcomeUnlockedBanner onDismiss={dismissWelcome} />}
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Sparkles className="h-6 w-6 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">AI Tax Strategy Advisor</h1>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid="plan-badge">
                Included in your plan
              </Badge>
            </div>
            
            {metrics && (
              <div className="text-sm text-gray-600" data-testid="header-progress">
                {interviewComplete ? (
                  'All applicable questions answered'
                ) : metrics.questions_allocated > 0 ? (
                  `${metrics.questions_answered} of ${metrics.questions_allocated} questions answered`
                ) : (
                  `Question ${coreStep} of 5 (Core)`
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Answered Questions Navigator */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <CardContent className="p-4">
                <h3 className="font-semibold text-gray-900 mb-4">Your Progress</h3>
                
                {/* Progress Bar */}
                {metrics && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2" data-testid="progress-label">
                      {interviewComplete ? (
                        <>
                          <span className="text-sm font-medium text-green-700">
                            100% Complete
                          </span>
                          <span className="text-xs text-gray-500">
                            {metrics.questions_answered} answered
                          </span>
                        </>
                      ) : metrics.questions_allocated > 0 ? (
                        <>
                          <span className="text-sm text-gray-600">
                            {metrics.completeness_for_gate.toFixed(1)}% Complete
                          </span>
                          <span className="text-xs text-gray-500">
                            {metrics.questions_answered}/{metrics.questions_allocated}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-sm text-gray-600">
                            Question {coreStep} of 5 (Core)
                          </span>
                          <span className="text-xs text-gray-500">
                            {coreAnswered}/5 answered
                          </span>
                        </>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2" data-testid="progress-bar">
                      <div 
                        data-testid="progress-bar-fill"
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: interviewComplete
                            ? '100%'
                            : metrics.questions_allocated > 0
                              ? `${Math.min(metrics.completeness_for_gate, 100)}%`
                              : `${(coreAnswered / 5) * 100}%`
                        }}
                      />
                    </div>
                    
                    {/* Milestones */}
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center text-sm">
                        <CheckCircle2 className={`h-4 w-4 mr-2 ${
                          metrics.questions_answered >= 5 ? 'text-green-500' : 'text-gray-400'
                        }`} />
                        <span className="text-gray-700">Core Questions</span>
                      </div>
                      {metrics.questions_allocated > 5 && (
                        <div className="flex items-center text-sm">
                          {interviewComplete ? (
                            <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                          ) : (
                            <Clock className={`h-4 w-4 mr-2 ${
                              metrics.questions_answered > 5 ? 'text-yellow-500' : 'text-gray-400'
                            }`} />
                          )}
                          <span className="text-gray-700">Conditional Modules</span>
                        </div>
                      )}
                      <div className="flex items-center text-sm">
                        <FileText className={`h-4 w-4 mr-2 ${
                          interviewComplete || metrics.questions_answered === metrics.questions_allocated ? 'text-green-500' : 'text-gray-400'
                        }`} />
                        <span className="text-gray-700">Final Questions</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Answered Questions List with Click-to-Edit */}
                {answeredQuestions.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Answered Questions</h4>
                    <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="answered-questions-list">
                      {answeredQuestions.map((q) => (
                        <button
                          key={q.question_id}
                          onClick={() => jumpToQuestion(q.question_id)}
                          data-testid={`answered-question-${q.question_id}`}
                          className="w-full text-left px-3 py-2 text-sm rounded hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-colors"
                        >
                          <div className="flex items-start">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">
                                Q{q.display_number || q.question_number}: {QUESTION_LABELS[q.question_id] || q.question_text}
                              </div>
                              <div className="text-xs text-gray-500 truncate" title={q.answer_display}>
                                {q.answer_display ?? (Array.isArray(q.answer) ? q.answer.join(', ') : q.answer)}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Click any question to review or edit
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Question Display */}
          <div className="lg:col-span-3">
            {currentQuestion ? (
              <Card className="mb-24">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <Badge variant="outline" className="mb-4">
                      Question {currentQuestion.display_number || currentQuestion.question_number || '?'}
                    </Badge>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4" data-testid="question-title" data-question-id={currentQuestion.question_id}>
                      {currentQuestion.question_text}
                    </h2>
                    {currentQuestion.help_text && (
                      <p className="text-gray-600 mb-6">
                        {currentQuestion.help_text}
                      </p>
                    )}
                  </div>

                  {/* Question Options - Card-based for core, radio/checkbox for conditionals */}
                  <div className="space-y-3">
                    {currentQuestion.question_type === 'single_select' && 
                     currentQuestion.category === 'core' ? (
                      // Card-based selection for core questions
                      <div className="grid grid-cols-1 gap-3">
                        {currentQuestion.options?.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setSelectedAnswer(option.value)}
                            data-testid={`option-${option.value}`}
                            className={`p-4 border-2 rounded-lg text-left transition-all ${
                              selectedAnswer === option.value
                                ? 'border-blue-600 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start">
                              <div className={`flex-shrink-0 mt-0.5 h-5 w-5 rounded-full border-2 ${
                                selectedAnswer === option.value
                                  ? 'border-blue-600 bg-blue-600'
                                  : 'border-gray-300'
                              }`}>
                                {selectedAnswer === option.value && (
                                  <CheckCircle2 className="h-4 w-4 text-white" />
                                )}
                              </div>
                              <div className="ml-3">
                                <div className="font-medium text-gray-900">{option.label}</div>
                                {option.description && (
                                  <div className="text-sm text-gray-500 mt-1">
                                    {option.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : currentQuestion.question_type === 'single_select' ? (
                      // Radio buttons for conditional questions
                      <div className="space-y-3">
                        {currentQuestion.options?.map((option) => (
                          <label
                            key={option.value}
                            className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                          >
                            <input
                              type="radio"
                              name={currentQuestion.question_id}
                              value={option.value}
                              checked={selectedAnswer === option.value}
                              onChange={(e) => setSelectedAnswer(e.target.value)}
                              data-testid={`option-${option.value}`}
                              className="mt-1"
                            />
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">{option.label}</div>
                              {option.description && (
                                <div className="text-sm text-gray-500 mt-1">
                                  {option.description}
                                </div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : currentQuestion.question_type === 'multi_select' ? (
                      // Checkboxes for multi-select
                      <div className="space-y-3">
                        {currentQuestion.options?.map((option) => (
                          <label
                            key={option.value}
                            className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              value={option.value}
                              checked={Array.isArray(selectedAnswer) && selectedAnswer.includes(option.value)}
                              onChange={(e) => toggleMultiSelect(option.value, e.target.checked)}
                              data-testid={`option-${option.value}`}
                              className="mt-1"
                            />
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">{option.label}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : currentQuestion.question_type === 'text' ? (
                      // Text area for open-ended
                      <textarea
                        value={selectedAnswer || ''}
                        onChange={(e) => setSelectedAnswer(e.target.value)}
                        placeholder="Enter your response..."
                        data-testid="question-text-input"
                        rows={4}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        maxLength={500}
                      />
                    ) : null}
                  </div>

                  {/* Submit Button */}
                  <div className="mt-8 flex justify-end gap-3">
                    {currentQuestion.question_type === 'text' && (
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={() => submitAnswer('')}
                        disabled={submitting}
                        data-testid="skip-question-btn"
                      >
                        Skip — nothing to add
                      </Button>
                    )}
                    <Button
                      onClick={() => submitAnswer()}
                      disabled={!isAnswerValid(currentQuestion, selectedAnswer) || submitting}
                      data-testid="continue-btn"
                      size="lg"
                      className="min-w-[200px]"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          Continue
                          <ChevronRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              // Interview complete
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Interview Complete!
                  </h2>
                  <p className="text-gray-600 mb-8">
                    You&apos;ve answered all applicable questions. Review your answers or generate your report.
                  </p>
                  
                  {metrics && !metrics.meets_threshold && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                        <div className="text-left">
                          <h3 className="font-semibold text-yellow-900 mb-1">
                            More answers needed
                          </h3>
                          <p className="text-sm text-yellow-800">
                            Complete at least 60% to generate a report. Currently: {metrics.completeness_for_gate}%
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              {metrics && (
                <>
                  <div className="font-semibold text-gray-900" data-testid="bottom-status">
                    {metrics.meets_threshold ? (
                      <span className="text-green-600">✓ Ready to generate report</span>
                    ) : metrics.questions_allocated > 0 ? (
                      <span className="text-gray-600">
                        {(() => {
                          const needed = Math.max(1, Math.ceil((0.6 * metrics.questions_allocated) - metrics.questions_answered));
                          return `${needed} more ${needed === 1 ? 'question' : 'questions'} needed`;
                        })()}
                      </span>
                    ) : (
                      <span className="text-gray-600">
                        {coreAnswered === 0
                          ? 'Answer the 5 core questions to begin'
                          : `${5 - coreAnswered} core ${5 - coreAnswered === 1 ? 'question' : 'questions'} left`}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {interviewComplete
                      ? 'Interview complete — all applicable questions answered'
                      : metrics.questions_allocated > 0
                        ? `${metrics.completeness_for_gate}% complete (60% required)`
                        : `Core questions: ${coreAnswered}/5 answered — report unlocks at 60% of your interview`}
                  </div>
                </>
              )}
            </div>
            
            <Button
              onClick={generateReport}
              disabled={!metrics?.meets_threshold || generating}
              aria-busy={generating}
              size="lg"
              className="min-w-[200px]"
              data-testid="generate-report-btn"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating report… (up to a minute)
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-5 w-5" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategyAdvisor;
